import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import logger from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Send, Copy, Check, Gift, User } from "lucide-react";

function formatMemberInput(raw) {
    const cleaned = (raw || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8);
    if (cleaned.length <= 4) return cleaned;
    return `${cleaned.slice(0, 4)}-${cleaned.slice(4)}`;
}

function isComplete(formatted) {
    return (formatted || "").replace("-", "").length === 8;
}

export default function SendCreditsCard({ balance, onTransferred }) {
    const [myMemberId, setMyMemberId] = useState("");
    const [copied, setCopied] = useState(false);
    const [open, setOpen] = useState(false);

    // dialog state machine
    const [step, setStep] = useState("lookup"); // lookup → confirm → sent
    const [recipientInput, setRecipientInput] = useState("");
    const [recipient, setRecipient] = useState(null);
    const [amount, setAmount] = useState(1);
    const [message, setMessage] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/me/member-id");
                setMyMemberId(r.data.member_id);
            } catch (e) {
                logger.warn("SendCredits", "could not load Member ID:", e?.message || e);
            }
        })();
    }, []);

    const copyMine = async () => {
        try {
            await navigator.clipboard.writeText(myMemberId);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            toast.error("Copy failed");
        }
    };

    const reset = () => {
        setStep("lookup");
        setRecipientInput("");
        setRecipient(null);
        setAmount(1);
        setMessage("");
        setBusy(false);
    };

    const onDialogChange = (o) => {
        setOpen(o);
        if (!o) reset();
    };

    const lookupRecipient = async () => {
        if (!isComplete(recipientInput)) return;
        setBusy(true);
        try {
            const r = await api.post("/transfers/lookup", { member_id: recipientInput.replace("-", "") });
            setRecipient(r.data);
            setStep("confirm");
        } catch (e) {
            toast.error("Lookup failed", { description: formatApiError(e.response?.data?.detail) || e.message });
        } finally {
            setBusy(false);
        }
    };

    const sendNow = async () => {
        if (!recipient || amount < 1) return;
        setBusy(true);
        try {
            await api.post("/transfers/send", {
                recipient_member_id: recipient.member_id.replace("-", ""),
                amount: parseInt(amount, 10),
                message: message.trim() || undefined,
            });
            toast.success("Credits sent", {
                description: `${amount} credit${amount !== 1 ? "s" : ""} sent to ${recipient.first_name}.`,
            });
            setStep("sent");
            if (typeof onTransferred === "function") onTransferred();
        } catch (e) {
            toast.error("Transfer failed", { description: formatApiError(e.response?.data?.detail) || e.message });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="bg-white border border-slate-200 rounded-md p-6 md:p-8" data-testid="send-credits-card">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                    <Gift className="h-5 w-5 text-slate-700" strokeWidth={1.75} />
                    <h2 className="font-heading text-xl font-bold text-slate-900">Send credits to a member</h2>
                </div>
                <Button
                    onClick={() => setOpen(true)}
                    disabled={!balance || balance < 1}
                    data-testid="open-send-credits-btn"
                    className="bg-brand hover:bg-brand-hover text-white rounded-md h-10 px-4"
                >
                    <Send className="h-4 w-4 mr-2" strokeWidth={1.75} />
                    Send credits
                </Button>
            </div>

            <p className="text-sm text-slate-600 mt-4 leading-relaxed max-w-xl">
                Share your Member ID with anyone who'd like to send you credits. Never share your email —
                your Member ID is the only thing they need.
            </p>

            <div className="mt-5 p-4 bg-slate-50 border border-slate-200 rounded-md flex items-center justify-between gap-4">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-slate-500">Your Member ID</div>
                    <div className="font-mono text-2xl tracking-[0.18em] font-bold text-slate-900 mt-1" data-testid="my-member-id">
                        {myMemberId || "—"}
                    </div>
                </div>
                <Button
                    type="button"
                    variant="outline"
                    onClick={copyMine}
                    disabled={!myMemberId}
                    data-testid="copy-member-id-btn"
                    className="rounded-md h-10 px-4"
                >
                    {copied ? <Check className="h-4 w-4 mr-1.5" strokeWidth={2} /> : <Copy className="h-4 w-4 mr-1.5" strokeWidth={1.75} />}
                    {copied ? "Copied" : "Copy"}
                </Button>
            </div>

            <Dialog open={open} onOpenChange={onDialogChange}>
                <DialogContent data-testid="send-credits-dialog" className="sm:max-w-md" aria-describedby={undefined}>
                    {step === "lookup" && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="font-heading text-xl text-slate-900">Send credits</DialogTitle>
                                <DialogDescription className="text-slate-600 pt-1">
                                    Enter the recipient's 8-character Member ID. You'll see their first name on the next step to confirm.
                                </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-3 mt-3">
                                <div>
                                    <Label className="text-xs uppercase tracking-[0.16em] font-mono text-slate-600">Recipient Member ID</Label>
                                    <Input
                                        value={recipientInput}
                                        onChange={(e) => setRecipientInput(formatMemberInput(e.target.value))}
                                        placeholder="ABCD-EFGH"
                                        data-testid="recipient-member-id-input"
                                        className="mt-1.5 h-11 bg-slate-50 border-slate-200 rounded-md focus-visible:ring-1 focus-visible:ring-brand focus-visible:border-brand font-mono tracking-[0.18em] uppercase"
                                        autoFocus
                                    />
                                </div>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => onDialogChange(false)} className="rounded-md">Cancel</Button>
                                <Button
                                    onClick={lookupRecipient}
                                    disabled={!isComplete(recipientInput) || busy}
                                    data-testid="lookup-member-btn"
                                    className="bg-brand hover:bg-brand-hover text-white rounded-md"
                                >
                                    {busy ? "Looking up…" : "Next"}
                                </Button>
                            </DialogFooter>
                        </>
                    )}

                    {step === "confirm" && recipient && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="font-heading text-xl text-slate-900">Confirm transfer</DialogTitle>
                                <DialogDescription className="text-slate-600 pt-1">
                                    For privacy we only show the recipient's first name. Verify it matches who you intend to send to.
                                </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4 mt-3">
                                <div className="p-4 bg-brand/5 border border-brand/30 rounded-md flex items-center gap-3" data-testid="confirm-recipient">
                                    <div className="h-10 w-10 rounded-full bg-brand text-white grid place-items-center font-heading font-bold">
                                        <User className="h-5 w-5" strokeWidth={1.75} />
                                    </div>
                                    <div>
                                        <div className="font-heading font-semibold text-slate-900">{recipient.first_name}</div>
                                        <div className="font-mono text-xs tracking-[0.16em] text-slate-500">{recipient.member_id}</div>
                                    </div>
                                </div>

                                <div>
                                    <Label className="text-xs uppercase tracking-[0.16em] font-mono text-slate-600">Amount (credits)</Label>
                                    <Input
                                        type="number"
                                        min={1}
                                        max={balance}
                                        value={amount}
                                        onChange={(e) => setAmount(Math.max(1, Math.min(balance, parseInt(e.target.value) || 1)))}
                                        data-testid="transfer-amount-input"
                                        className="mt-1.5 h-11 bg-slate-50 border-slate-200 rounded-md focus-visible:ring-1 focus-visible:ring-brand focus-visible:border-brand"
                                    />
                                    <p className="text-xs text-slate-500 mt-1.5">Your balance: <span className="font-mono">{balance}</span></p>
                                </div>

                                <div>
                                    <Label className="text-xs uppercase tracking-[0.16em] font-mono text-slate-600">Message (optional)</Label>
                                    <textarea
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value.slice(0, 280))}
                                        placeholder="A short note for the recipient…"
                                        data-testid="transfer-message-input"
                                        rows={3}
                                        className="mt-1.5 w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand focus-visible:border-brand"
                                    />
                                    <p className="text-xs text-slate-400 mt-1 text-right">{message.length}/280</p>
                                </div>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setStep("lookup")} className="rounded-md">Back</Button>
                                <Button
                                    onClick={sendNow}
                                    disabled={busy || amount < 1 || amount > balance}
                                    data-testid="confirm-send-btn"
                                    className="bg-brand hover:bg-brand-hover text-white rounded-md"
                                >
                                    {busy ? "Sending…" : `Send ${amount} credit${amount !== 1 ? "s" : ""}`}
                                </Button>
                            </DialogFooter>
                        </>
                    )}

                    {step === "sent" && recipient && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="font-heading text-xl text-slate-900">Credits sent</DialogTitle>
                                <DialogDescription className="text-slate-600 pt-1">
                                    {amount} credit{amount !== 1 ? "s" : ""} are now on {recipient.first_name}'s account. They'll get an email and an in-app notification.
                                </DialogDescription>
                            </DialogHeader>
                            <DialogFooter>
                                <Button onClick={() => onDialogChange(false)} data-testid="send-done-btn" className="bg-brand hover:bg-brand-hover text-white rounded-md w-full">
                                    Done
                                </Button>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
